import { inject, Injectable } from '@angular/core';
import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { AuthService } from './auth-service';
import { catchError, EMPTY, filter, finalize, Observable, Subject, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { ApiService } from './api-service';
import { AuthToken } from '../types/auth-token';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private readonly auth = inject(AuthService);
  private readonly api = inject(ApiService);
  private refreshTokenInProgress = false;
  private tokenSubject = new Subject<boolean>();

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (
      this.auth.isAuthenticated() &&
      !req.url.startsWith(environment.toolboxApiEndpoint + '/auth')
    ) {
      req = this.addAccessToken(req);
    }

    return next.handle(req).pipe(
      catchError((error: unknown) => {
        if (
          error instanceof HttpErrorResponse &&
          error.status === 401 &&
          this.auth.isAuthenticated()
        ) {
          return this.handle401(req, next);
        }

        return throwError(() => error);
      }),
    );
  }

  private handle401(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    if (!this.refreshTokenInProgress) {
      this.refreshTokenInProgress = true;
      this.tokenSubject.next(false);

      return this.api.refreshAccessToken(this.auth.getRefreshToken() || '').pipe(
        catchError(() => {
          this.auth.logout();

          return EMPTY;
        }),
        switchMap((token: AuthToken) => {
          this.tokenSubject.next(true);
          this.auth.setAuthToken(token);
          const newRequest = this.addAccessToken(request);

          return next.handle(newRequest);
        }),
        finalize(() => {
          this.refreshTokenInProgress = false;
        }),
      );
    } else {
      return this.tokenSubject.pipe(
        filter((refreshed) => refreshed),
        take(1),
        switchMap(() => {
          const newRequest = this.addAccessToken(request);

          return next.handle(newRequest);
        }),
      );
    }
  }

  addAccessToken(request: HttpRequest<unknown>): HttpRequest<unknown> {
    request = request.clone({
      setHeaders: {
        Authorization: `Bearer ${this.auth.getAccessToken()}`,
      },
    });

    return request;
  }
}
